/**
 * Response Interceptor for ArbiWin API Client
 *
 * This module handles all incoming HTTP responses, including:
 * - Response processing and transformation
 * - Error response handling and recovery
 * - Authentication error handling (401)
 * - Subscription error handling (402)
 * - Response logging and monitoring
 * - Performance tracking
 *
 * Following constitution requirements for API integration
 * and error handling best practices.
 */

import { logError } from '../../../utils/errorHandler';
import { getEnvironmentConfig } from '../../../config/environment';
import { TokenManager } from './TokenManager';

/**
 * HTTP Status codes for response handling
 */
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
};

/**
 * Response performance tracking
 */
const ResponsePerformanceTracker = {
  responses: new Map(),

  /**
   * Start tracking response time
   * @param {string} requestId - Request ID
   * @param {number} startTime - Request start time
   */
  startTracking(requestId, startTime) {
    this.responses.set(requestId, {
      startTime,
      endTime: null,
      duration: null,
      status: null,
      size: null
    });
  },

  /**
   * End tracking and calculate metrics
   * @param {string} requestId - Request ID
   * @param {Object} response - Axios response object
   */
  endTracking(requestId, response) {
    const tracking = this.responses.get(requestId);
    if (tracking) {
      tracking.endTime = Date.now();
      tracking.duration = tracking.endTime - tracking.startTime;
      tracking.status = response.status;
      tracking.size = this.calculateResponseSize(response);

      // Log slow responses
      if (tracking.duration > 3000) { // 3 seconds
        console.warn(`🐌 Slow API response: ${requestId} took ${tracking.duration}ms`);
      }

      // Clean up old entries after 1 minute
      setTimeout(() => {
        this.responses.delete(requestId);
      }, 60000);
    }
  },

  /**
   * Calculate approximate response size
   * @param {Object} response - Response object
   * @returns {number} Approximate size in bytes
   */
  calculateResponseSize(response) {
    try {
      if (response.data) {
        return JSON.stringify(response.data).length;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  },

  /**
   * Get performance statistics
   * @returns {Object|null} Performance stats
   */
  getStats() {
    const completedResponses = Array.from(this.responses.values())
      .filter(r => r.duration !== null);

    if (completedResponses.length === 0) return null;

    const durations = completedResponses.map(r => r.duration);
    const sizes = completedResponses.map(r => r.size || 0);

    return {
      count: completedResponses.length,
      averageResponseTime: durations.reduce((a, b) => a + b, 0) / durations.length,
      minResponseTime: Math.min(...durations),
      maxResponseTime: Math.max(...durations),
      averageSize: sizes.reduce((a, b) => a + b, 0) / sizes.length,
      totalSize: sizes.reduce((a, b) => a + b, 0)
    };
  }
};

/**
 * Response data transformer
 */
const ResponseTransformer = {
  /**
   * Transform response data based on content type
   * @param {Object} response - Axios response object
   * @returns {any} Transformed data
   */
  transformResponse(response) {
    const contentType = response.headers['content-type'] || '';

    // Handle JSON responses
    if (contentType.includes('application/json')) {
      return this.transformJsonResponse(response);
    }

    // Handle text responses
    if (contentType.includes('text/')) {
      return response.data;
    }

    // Handle blob responses (files, images)
    if (contentType.includes('application/octet-stream') ||
        contentType.includes('image/') ||
        contentType.includes('application/pdf')) {
      return response.data; // Return as blob
    }

    // Default: return data as-is
    return response.data;
  },

  /**
   * Transform JSON response data
   * @param {Object} response - Response object
   * @returns {any} Transformed JSON data
   */
  transformJsonResponse(response) {
    const data = response.data;

    // Handle standard API response format
    if (data && typeof data === 'object') {
      // Add response metadata
      if (data.data !== undefined) {
        // Standard format: { data: ..., message: ..., success: ... }
        return {
          ...data,
          _metadata: {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            timestamp: new Date().toISOString()
          }
        };
      }
    }

    return data;
  }
};

/**
 * Error response handler
 */
const ErrorResponseHandler = {
  /**
   * Handle different types of API errors
   * @param {Object} error - Axios error object
   * @returns {Promise<Error>} Processed error
   */
  async handleError(error) {
    const envConfig = getEnvironmentConfig();

    // Extract request information
    const requestId = error.config?.metadata?.requestId;
    const startTime = error.config?.metadata?.startTime;

    // End performance tracking
    if (requestId && startTime) {
      ResponsePerformanceTracker.endTracking(requestId, {
        status: error.response?.status || 0,
        data: error.response?.data
      });
    }

    // Development logging
    if (envConfig.debug.enabled) {
      this.logError(error, requestId, startTime);
    }

    // Handle specific error types
    if (error.response) {
      return this.handleHttpError(error);
    } else if (error.request) {
      return this.handleNetworkError(error);
    } else {
      return this.handleRequestSetupError(error);
    }
  },

  /**
   * Handle HTTP error responses
   * @param {Object} error - Error with response
   * @returns {Promise<Error>} Processed error
   */
  async handleHttpError(error) {
    const status = error.response.status;

    // Handle specific status codes
    switch (status) {
      case HTTP_STATUS.UNAUTHORIZED:
        return this.handleUnauthorizedError(error);

      case HTTP_STATUS.PAYMENT_REQUIRED:
        return this.handlePaymentRequiredError(error);

      case HTTP_STATUS.FORBIDDEN:
        return this.handleForbiddenError(error);

      case HTTP_STATUS.NOT_FOUND:
        return this.handleNotFoundError(error);

      case HTTP_STATUS.TOO_MANY_REQUESTS:
        return this.handleRateLimitError(error);

      case HTTP_STATUS.BAD_REQUEST:
        return this.handleValidationError(error);

      case HTTP_STATUS.UNPROCESSABLE_ENTITY:
        return this.handleValidationError(error);

      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      case HTTP_STATUS.BAD_GATEWAY:
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
      case HTTP_STATUS.GATEWAY_TIMEOUT:
        return this.handleServerError(error);

      default:
        return this.handleGenericHttpError(error);
    }
  },

  /**
   * Handle 401 Unauthorized errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleUnauthorizedError(error) {
    const errorData = error.response?.data || {};
    const detail = errorData.detail || errorData.message || '';
    const description = errorData.description || '';
    const detailLower = detail.toLowerCase();
    const descriptionLower = description.toLowerCase();
    
    // Combine detail and description for checking
    const combinedText = `${detail} ${description}`.toLowerCase();
    
    console.warn('🔒 Authentication required - clearing tokens');
    console.log('🔍 ResponseInterceptor: 401 Error details:', { 
      detail, 
      description,
      errorData,
      status: errorData.status,
      type: errorData.type,
      title: errorData.title
    });
    console.log('🔍 ResponseInterceptor: Checking for session conflict patterns...');

    // Check for session conflict scenarios - check both detail and description
    const isSessionConflict = combinedText.includes('invalid or inactive token') ||
                             combinedText.includes('concurrent') || 
                             (combinedText.includes('session') && 
                             (combinedText.includes('conflict') || combinedText.includes('terminated')));
    
    const isSessionTerminated = combinedText.includes('invalid or inactive token') ||
                               combinedText.includes('inactive') || 
                               combinedText.includes('terminated') ||
                               combinedText.includes('revoked') ||
                               combinedText.includes('token is invalid') ||
                               combinedText.includes('token has been revoked');

    console.log('🔍 ResponseInterceptor: Session conflict detection:', {
      isSessionConflict,
      isSessionTerminated,
      detailLower,
      descriptionLower,
      combinedText,
      matchesInvalidOrInactive: combinedText.includes('invalid or inactive token'),
      matchesConcurrent: combinedText.includes('concurrent'),
      matchesSessionConflict: combinedText.includes('session') && (combinedText.includes('conflict') || combinedText.includes('terminated'))
    });

    // Clear invalid tokens
    TokenManager.clearTokens();

    if (isSessionConflict || isSessionTerminated) {
      // Handle session conflict/termination
      const reason = isSessionConflict ? 'session_conflict' : 'session_terminated';
      const message = isSessionConflict ? 
        'Another session is active. Please log in again.' : 
        'Session has been terminated. Please log in again.';

      console.log('🔍 ResponseInterceptor: Session conflict detected:', { reason, message });

      // Dispatch session conflict event
      window.dispatchEvent(new CustomEvent('auth:sessionConflict', {
        detail: {
          reason,
          message,
          endpoint: error.config?.url,
          status: 401,
          timestamp: new Date().toISOString()
        }
      }));

      // Dispatch logout event to clear user state
      window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: {
          success: false,
          reason,
          message
        }
      }));

      return this.createStandardizedError(error, {
        code: 'SESSION_CONFLICT',
        message,
        recoverable: true,
        recovery: 'login'
      });
    } else {
      // Regular authentication required
      window.dispatchEvent(new CustomEvent('auth:required', {
        detail: {
          reason: 'unauthorized',
          endpoint: error.config?.url,
          status: 401
        }
      }));

      return this.createStandardizedError(error, {
        code: 'UNAUTHORIZED',
        message: 'Authentication required. Please log in again.',
        recoverable: true,
        recovery: 'login'
      });
    }
  },

  /**
   * Handle 402 Payment Required errors (subscription)
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handlePaymentRequiredError(error) {
    const endpoint = error.config?.url;
    
    // Define authentication endpoints that should not trigger payment required modal
    const authEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/logout',
      '/auth/refresh-token',
      '/auth/validate-session'
    ];
    
    // Check if this is an authentication endpoint
    const isAuthEndpoint = authEndpoints.some(authEndpoint => 
      endpoint && endpoint.includes(authEndpoint)
    );
    
    if (isAuthEndpoint) {
      console.warn('💳 HTTP 402 on authentication endpoint - not triggering payment modal');
      
      // For auth endpoints, just return the error without triggering payment modal
      return this.createStandardizedError(error, {
        code: 'AUTH_PAYMENT_REQUIRED',
        message: 'Payment required for authentication.',
        recoverable: false,
        recovery: 'contact_support'
      });
    }

    if (endpoint && endpoint.includes('/markets-analysis/near-arb')) {
      return this.createStandardizedError(error, {
        code: 'ULTIMATE_REQUIRED',
        message: 'Ultimate subscription required',
        recoverable: false,
        recovery: 'none'
      });
    }

    console.warn('💳 Subscription required for this feature');

    // Dispatch subscription required event only for non-auth endpoints
    window.dispatchEvent(new CustomEvent('subscription:required', {
      detail: {
        endpoint: error.config?.url,
        feature: this.extractFeatureFromUrl(error.config?.url),
        status: 402
      }
    }));

    return this.createStandardizedError(error, {
      code: 'SUBSCRIPTION_REQUIRED',
      message: 'Active subscription required to access this feature.',
      recoverable: true,
      recovery: 'activate_subscription'
    });
  },

  /**
   * Handle 403 Forbidden errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleForbiddenError(error) {
    return this.createStandardizedError(error, {
      code: 'FORBIDDEN',
      message: 'You do not have permission to access this resource.',
      recoverable: false,
      recovery: 'contact_admin'
    });
  },

  /**
   * Handle 404 Not Found errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleNotFoundError(error) {
    return this.createStandardizedError(error, {
      code: 'NOT_FOUND',
      message: 'The requested resource was not found.',
      recoverable: true,
      recovery: 'retry'
    });
  },

  /**
   * Handle 429 Rate Limit errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleRateLimitError(error) {
    const retryAfter = error.response.headers['retry-after'];
    const retryAfterMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 60000;

    return this.createStandardizedError(error, {
      code: 'RATE_LIMITED',
      message: `Too many requests. Please try again in ${Math.ceil(retryAfterMs / 1000)} seconds.`,
      recoverable: true,
      recovery: 'retry_after_delay',
      retryAfter: retryAfterMs
    });
  },

  /**
   * Handle 422 Validation errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleValidationError(error) {
    const data = error.response.data;
    const validationErrors = data?.errors || data?.validationErrors || {};

    console.log('🔍 ResponseInterceptor.handleValidationError: Processing error:', {
      status: error.response.status,
      data: data,
      detail: data?.detail,
      message: data?.message,
      fullError: error
    });

    const errorMessage = data?.detail || data?.message || 'Validation failed.';
    console.log('🔍 ResponseInterceptor.handleValidationError: Final message:', errorMessage);

    return this.createStandardizedError(error, {
      code: 'VALIDATION_ERROR',
      message: errorMessage,
      recoverable: true,
      recovery: 'fix_validation',
      validationErrors
    });
  },

  /**
   * Handle server errors (5xx)
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleServerError(error) {
    const status = error.response.status;

    return this.createStandardizedError(error, {
      code: 'SERVER_ERROR',
      message: 'Server error occurred. Please try again later.',
      recoverable: true,
      recovery: 'retry',
      serverStatus: status
    });
  },

  /**
   * Handle generic HTTP errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleGenericHttpError(error) {
    const status = error.response.status;
    const data = error.response.data;

    return this.createStandardizedError(error, {
      code: 'HTTP_ERROR',
      message: data?.message || error.message || `HTTP ${status} error occurred.`,
      recoverable: true,
      recovery: 'retry'
    });
  },

  /**
   * Handle network errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleNetworkError(error) {
    return this.createStandardizedError(error, {
      code: 'NETWORK_ERROR',
      message: 'Network error. Please check your internet connection.',
      recoverable: true,
      recovery: 'check_connection'
    });
  },

  /**
   * Handle request setup errors
   * @param {Object} error - Error object
   * @returns {Promise<Error>} Processed error
   */
  async handleRequestSetupError(error) {
    return this.createStandardizedError(error, {
      code: 'REQUEST_SETUP_ERROR',
      message: error.message || 'Request setup failed.',
      recoverable: false,
      recovery: 'contact_support'
    });
  },

  /**
   * Create standardized error object
   * @param {Object} originalError - Original error
   * @param {Object} errorInfo - Additional error information
   * @returns {Error} Standardized error
   */
  createStandardizedError(originalError, errorInfo) {
    const error = new Error(errorInfo.message);

    // Add standardized properties
    error.status = originalError.response?.status || 0;
    error.code = errorInfo.code;
    error.data = originalError.response?.data;
    error.url = originalError.config?.url;
    error.method = originalError.config?.method?.toUpperCase();
    error.timestamp = new Date().toISOString();
    error.requestId = originalError.config?.metadata?.requestId;
    error.recoverable = errorInfo.recoverable;
    error.recovery = errorInfo.recovery;

    // Add additional error-specific properties
    Object.keys(errorInfo).forEach(key => {
      if (!error.hasOwnProperty(key)) {
        error[key] = errorInfo[key];
      }
    });

    // Log error for monitoring
    logError(error, 'API_RESPONSE_ERROR', {
      endpoint: error.url,
      method: error.method,
      status: error.status,
      code: error.code
    });

    return error;
  },

  /**
   * Extract feature name from URL for subscription errors
   * @param {string} url - Request URL
   * @returns {string} Feature name
   */
  extractFeatureFromUrl(url) {
    if (!url) return 'unknown';

    if (url.includes('/api/core/markets-analysis')) return 'surebet_data';
    if (url.includes('/api/users/profile')) return 'profile_access';
    if (url.includes('/api/subscriptions')) return 'subscription_management';

    return 'premium_feature';
  },

  /**
   * Log error details for development
   * @param {Object} error - Error object
   * @param {string} requestId - Request ID
   * @param {number} startTime - Request start time
   */
  logError(error, requestId, startTime) {
    const duration = startTime ? Date.now() - startTime : 0;

    console.group(`❌ API Error: ${requestId} (${duration}ms)`);
    console.log('Status:', error.response?.status);
    console.log('URL:', error.config?.url);
    console.log('Method:', error.config?.method?.toUpperCase());
    console.log('Error:', error.message);
    if (error.response?.data) console.log('Response Data:', error.response.data);
    if (error.response?.headers) console.log('Response Headers:', error.response.headers);
    console.groupEnd();
  }
};

/**
 * Create response interceptor function
 * @returns {Function} Response interceptor
 */
export const createResponseInterceptor = () => {
  /**
   * Response success handler
   * @param {Object} response - Axios response object
   * @returns {any} Processed response data
   */
  return function(response) {
    const envConfig = getEnvironmentConfig();
    const requestId = response.config?.metadata?.requestId;
    const startTime = response.config?.metadata?.startTime;

    // Check for error status codes (4xx, 5xx)
    if (response.status >= 400) {
      // Create an error object that will be caught by the error handler
      const error = new Error(`HTTP ${response.status} ${response.statusText}`);
      error.response = response;
      error.config = response.config;
      error.request = response.request;
      throw error;
    }

    // End performance tracking
    if (requestId) {
      ResponsePerformanceTracker.endTracking(requestId, response);
    }

    // Development logging
    if (envConfig.debug.enabled) {
      logSuccessResponse(response, requestId, startTime);
    }

    // Transform response data
    const transformedData = ResponseTransformer.transformResponse(response);

    // Handle special response cases
    handleSpecialResponses(response);

    // Return transformed data (not the full response object)
    return transformedData;
  };
};

/**
 * Create response error handler
 * @returns {Function} Response error handler
 */
export const createResponseErrorHandler = () => {
  return async (error) => {
    console.log('🔍 ResponseInterceptor.createResponseErrorHandler: Error caught:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      hasResponse: !!error.response,
      hasConfig: !!error.config,
      fullError: error
    });
    
    // Handle 401 errors immediately before other processing
    if (error.response?.status === 401) {
      console.log('🚨 ResponseInterceptor: 401 detected, checking for invalid token...');
      const errorData = error.response?.data || {};
      const detail = errorData.detail || errorData.message || '';
      const description = errorData.description || '';
      const combinedText = `${detail} ${description}`.toLowerCase();
      
      console.log('🚨 ResponseInterceptor: 401 error data:', {
        detail,
        description,
        combinedText,
        errorData
      });
      
      // Check if this is an invalid/inactive token error
      if (combinedText.includes('invalid or inactive token') ||
          combinedText.includes('inactive') ||
          combinedText.includes('revoked') ||
          combinedText.includes('token is invalid') ||
          combinedText.includes('token has been revoked')) {
        console.log('🚨 ResponseInterceptor: Invalid token detected, dispatching sessionConflict event');
        
        // Dispatch session conflict event immediately
        window.dispatchEvent(new CustomEvent('auth:sessionConflict', {
          detail: {
            reason: 'session_invalid',
            message: 'Your session is not valid or has been revoked. Please log in again.',
            endpoint: error.config?.url,
            status: 401,
            timestamp: new Date().toISOString(),
            errorData: errorData
          }
        }));
      }
    }
    
    return await ErrorResponseHandler.handleError(error);
  };
};

/**
 * Log successful response for development
 * @param {Object} response - Response object
 * @param {string} requestId - Request ID
 * @param {number} startTime - Request start time
 */
function logSuccessResponse(response, requestId, startTime) {
  const duration = startTime ? Date.now() - startTime : 0;

  console.group(`✅ API Response: ${requestId} (${duration}ms)`);
  console.log('Status:', response.status, response.statusText);
  console.log('URL:', response.config?.url);
  console.log('Method:', response.config?.method?.toUpperCase());
  console.log('Headers:', response.headers);
  console.log('Data:', response.data);
  console.groupEnd();
}

/**
 * Handle special response cases
 * @param {Object} response - Response object
 */
function handleSpecialResponses(response) {
  // Handle authentication token updates
  if (response.headers['x-new-token']) {
    const newToken = response.headers['x-new-token'];
    const expiresIn = parseInt(response.headers['x-token-expires'] || '1800', 10);

    TokenManager.setTokens(newToken, TokenManager.getRefreshToken(), expiresIn);
    console.log('Token updated from response headers');
  }

  // Handle session updates
  if (response.headers['x-session-id']) {
    sessionStorage.setItem('arbiwin_session_id', response.headers['x-session-id']);
  }
}

/**
 * Export performance tracker for monitoring
 */
export { ResponsePerformanceTracker, ErrorResponseHandler };

/**
 * Export interceptor factory functions
 */
export default {
  createResponseInterceptor,
  createResponseErrorHandler
};
